import { useState } from 'react';
import { Inbox, Search, X } from 'lucide-react';
import { Page, PageHeader } from '@ampm-7en-ai/ui/components/Page';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Input } from '@ampm-7en-ai/ui/components/Input';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { ListCard, ListRow, ListRowSkeleton } from '@ampm-7en-ai/ui/components/ListRow';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { CONTACT_STATUSES, contactLabel, contactName, useContactRequests } from '@/hooks/useContactRequests';

export default function ContactRequestsV2() {
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, error, refetch } = useContactRequests({ search, status, page });
  const filtered = !!search || status !== 'all';

  return (
    <Page>
      <PageHeader title="Contact requests" description="Messages sent through the contact form."
        meta={data && !isError ? <Pill>{data.count.toLocaleString()}</Pill> : undefined} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form className="relative min-w-[14rem] flex-1" onSubmit={(event) => {
          event.preventDefault();
          setSearch(draft.trim());
          setPage(1);
        }}>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-fg-subtle" aria-hidden />
          <Input aria-label="Search contact requests" placeholder="Search name, email or message"
            value={draft} onChange={(event) => setDraft(event.target.value)} className="pl-9 pr-10" />
          {draft && <button type="button" aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-ui-sm p-1 text-ui-fg-muted focus-visible:ring-2 focus-visible:ring-ui-ring"
            onClick={() => { setDraft(''); setSearch(''); setPage(1); }}>
            <X className="h-4 w-4" aria-hidden />
          </button>}
        </form>
        <Button onClick={() => { setSearch(draft.trim()); setPage(1); }}>Search</Button>
        <Select aria-label="Request status" className="w-44" value={status}
          options={[{ value: 'all', label: 'All statuses' }, ...CONTACT_STATUSES]}
          onValueChange={(value) => { setStatus(value); setPage(1); }} />
      </div>

      {isError ? (
        <Callout tone="danger" title="Could not load contact requests">
          <p>{error instanceof Error ? error.message : 'The request failed.'}</p>
          <Button className="mt-3" onClick={() => void refetch()}>Try again</Button>
        </Callout>
      ) : isLoading ? (
        <ListCard>{Array.from({ length: 5 }, (_, index) => <li key={index}><ListRowSkeleton /></li>)}</ListCard>
      ) : !data?.items.length ? (
        <Panel variant="dashed"><EmptyState icon={Inbox}
          title={filtered ? 'No matching requests' : 'No contact requests'}
          description={filtered ? 'Try another search or status.' : 'Messages sent through the contact form will appear here.'} /></Panel>
      ) : (
        <ListCard>
          {data.items.map((request) => (
            <li key={request.id}>
              <ListRow href={`/v2/contact-requests/${request.id}`} title={contactLabel(request)}
                // `line-clamp` sets its own display, so it cannot share a span with `block`.
                description={request.message ? <span className="line-clamp-2">{request.message}</span> : undefined}
                footer={<span>
                  {`#${request.id}`
                    + `${request.email && contactName(request) ? ` · ${request.email}` : ''}`
                    + ` · ${new Date(request.created_at).toLocaleString()}`}
                </span>}
                meta={<Pill tone={request.status === 'RESOLVED' ? 'success' : request.status === 'OPEN' ? 'warning' : 'neutral'}>
                  {CONTACT_STATUSES.find((status) => status.value === request.status)?.label}
                </Pill>} />
            </li>
          ))}
        </ListCard>
      )}
      {data && !isError && (data.next || data.previous) && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-ui-xs text-ui-fg-muted">Page {page} · {data.count.toLocaleString()} requests</p>
          <div className="flex gap-2">
            <Button disabled={!data.previous || isFetching} onClick={() => setPage((page) => Math.max(1, page - 1))}>Previous</Button>
            <Button disabled={!data.next || isFetching} onClick={() => setPage((page) => page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </Page>
  );
}
